import { api } from './api'
import axios from 'axios'
import { getTenantApiUrl, getTenantId } from '../utils/tenant'
import { authService } from './authService'
import { PatientFile, PatientFileUpload, PatientFileFilters } from '../types/patientFile'

export const patientFileService = {
  /**
   * Get all patient files with optional filters
   */
  getAll: async (filters?: PatientFileFilters): Promise<PatientFile[]> => {
    const response = await api.get<PatientFile[]>('/patient-files', {
      params: filters,
    })
    return response.data
  },

  /**
   * Get patient file by ID
   */
  getById: async (id: string): Promise<PatientFile> => {
    const response = await api.get<PatientFile>(`/patient-files/${id}`)
    return response.data
  },

  /**
   * Get files for a specific patient
   */
  getByPatient: async (patientId: string): Promise<PatientFile[]> => {
    const response = await api.get<PatientFile[]>(`/patient-files`, {
      params: { patientId },
    })
    return response.data
  },

  /**
   * Upload a new file for a patient using direct S3 upload (bypasses API Gateway 10MB limit)
   */
  upload: async (patientId: string, upload: PatientFileUpload): Promise<PatientFile> => {
    // Step 1: Initialize upload and get presigned POST URL
    const initResponse = await api.post<{
      fileId: string
      s3Key: string
      uploadUrl: string
      fields: Record<string, string>
    }>(`/patient-files/upload/init`, {
      fileName: upload.file.name,
      fileSize: upload.file.size,
      fileType: upload.file.type || 'application/octet-stream',
    }, {
      params: { patientId },
    })

    const { fileId, s3Key, uploadUrl, fields } = initResponse.data

    // Step 2: Upload file directly to S3 using presigned POST URL
    // Skip actual S3 upload in mock/development mode (when URL contains 'mock' or 'example.com')
    const isMockMode = uploadUrl.includes('mock') || uploadUrl.includes('example.com')
    
    if (!isMockMode) {
      const s3FormData = new FormData()
      // Add all fields from presigned POST (must be added before file)
      Object.entries(fields).forEach(([key, value]) => {
        s3FormData.append(key, value)
      })
      // Add the file last (required by S3 presigned POST)
      // When appending a File object, the browser will set Content-Type automatically
      // If the file has a type, it will be used; otherwise the field's Content-Type will be used
      // This ensures binary data is uploaded correctly without corruption
      s3FormData.append('file', upload.file)

      // Log origin for debugging CORS issues
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'unknown'
      console.log('[PatientFileService] Uploading to S3:', {
        uploadUrl,
        origin: currentOrigin,
        fileSize: upload.file.size,
        fileType: upload.file.type,
      })

      // Upload to S3 using presigned POST URL
      // Note: Don't set Content-Type header - browser will set it automatically with boundary
      // This is required for multipart/form-data uploads
      try {
        const s3UploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: s3FormData,
          // Don't set credentials or mode - let browser handle CORS naturally
          // The presigned POST URL includes all necessary authentication
        })

        if (!s3UploadResponse.ok) {
          const errorText = await s3UploadResponse.text()
          console.error('[PatientFileService] S3 upload failed:', {
            status: s3UploadResponse.status,
            statusText: s3UploadResponse.statusText,
            error: errorText,
            origin: currentOrigin,
          })
          throw new Error(`S3 upload failed: ${s3UploadResponse.status} ${errorText}`)
        }
      } catch (error: any) {
        // Enhanced error logging for CORS issues
        if (error.message?.includes('CORS') || error.name === 'TypeError') {
          console.error('[PatientFileService] CORS error detected:', {
            error: error.message,
            origin: currentOrigin,
            uploadUrl,
            suggestion: 'Ensure the frontend origin is in the S3 bucket CORS configuration',
          })
        }
        throw error
      }
    }

    // Step 3: Complete upload and save metadata
    const completeResponse = await api.post<PatientFile>(`/patient-files/upload/complete`, {
      fileId,
      s3Key,
      fileName: upload.file.name,
      fileType: upload.file.type || 'application/octet-stream',
      fileSize: upload.file.size,
      displayName: upload.displayName,
      description: upload.description,
      category: upload.category,
      providerCustomNote: upload.providerCustomNote,
    }, {
      params: { patientId },
    })

    return completeResponse.data
  },

  /**
   * Update a patient file (metadata only)
   */
  update: async (
    id: string,
    data: Partial<Pick<PatientFile, 'displayName' | 'description' | 'category' | 'taskIds' | 'source'>>
  ): Promise<PatientFile> => {
    const response = await api.put<PatientFile>(`/patient-files/${id}`, data)
    return response.data
  },

  /**
   * Get file view URL (for browser viewing)
   * Always use secure download endpoint - never use presigned URLs for PHI security
   */
  getViewUrl: (file: PatientFile): string => {
    return `/api/patient-files/${file.id}/download`
  },

  /**
   * Delete a patient file
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/patient-files/${id}`)
  },

  /**
   * Download a patient file
   * Returns a Blob by fetching through authenticated Lambda endpoint
   * Files are proxied through Lambda for security (PHI must not be publicly accessible)
   * Uses axios directly to ensure blob responseType is handled correctly
   */
  download: async (id: string): Promise<Blob> => {
    try {
      // Use axios directly for blob responses to ensure proper handling
      const apiUrl = getTenantApiUrl(import.meta.env.VITE_API_URL || 'https://api.clnicl.example.com')
      const token = authService.getToken()
      const tenantId = getTenantId()
      
      // Get response as text first to check if it's base64-encoded
      // API Gateway should decode base64 automatically, but doesn't always work with Lambda proxy
      const response = await axios.get(`${apiUrl}/patient-files/${id}/download`, {
        responseType: 'text', // Get as text to check for base64
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'x-tenant-id': tenantId || '',
        },
      })
      
      const contentType = response.headers['content-type'] || 'application/octet-stream'
      const responseText = response.data as string
      
      // Simple check: if it looks like base64 (valid base64 chars, reasonable length), decode it
      // Real binary data would have null bytes or non-printable chars when interpreted as text
      const looksLikeBase64 = /^[A-Za-z0-9+/=\s]+$/.test(responseText) && 
                              responseText.length > 50 &&
                              !responseText.includes('\0')
      
      let blob: Blob
      
      if (looksLikeBase64) {
        // API Gateway didn't decode it - decode base64 ourselves
        console.log('[PatientFileService] Decoding base64-encoded response...')
        try {
          const binaryString = atob(responseText.trim())
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          blob = new Blob([bytes], { type: contentType })
        } catch (e) {
          // If decoding fails, it might already be decoded - use as-is
          console.log('[PatientFileService] Base64 decode failed, using response as-is')
          blob = new Blob([responseText], { type: contentType })
        }
      } else {
        // Already decoded or not base64 - use as-is
        blob = new Blob([responseText], { type: contentType })
      }
      
      // Debug logging
      console.log('[PatientFileService] Downloaded file:', {
        id,
        blobSize: blob.size,
        blobType: blob.type,
        contentType,
      })
      
      if (blob.size === 0) {
        console.warn('[PatientFileService] Warning: Downloaded blob is empty')
        throw new Error('Downloaded file is empty')
      }
      
      return blob
    } catch (error) {
      console.error('[PatientFileService] Download error:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to download file')
    }
  },

  /**
   * Get file download URL (for direct access)
   * Always use secure download endpoint - never use presigned URLs for PHI security
   */
  getDownloadUrl: (file: PatientFile): string => {
    return `/api/patient-files/${file.id}/download`
  },

  /**
   * Link a file to a task
   */
  linkToTask: async (fileId: string, taskId: string): Promise<PatientFile> => {
    const file = await patientFileService.getById(fileId)
    const taskIds = file.taskIds || []
    if (!taskIds.includes(taskId)) {
      taskIds.push(taskId)
    }
    return patientFileService.update(fileId, { taskIds })
  },

  /**
   * Get files linked to a specific task
   */
  getByTask: async (taskId: string): Promise<PatientFile[]> => {
    const response = await api.get<PatientFile[]>('/patient-files', {
      params: { taskId },
    })
    return response.data
  },
}






























